import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isVideo } from '@application/helpers/video-detection-helper';
import { filterApplicationLanguages } from '@application/helpers/language-filter-helper';
import { Box, Chip, IconButton, Typography, useTheme } from '@mui/material';
import EventPreviewThumbnail from '@public/assets/images/thumbnail/thumbnail_1.png';
import HTimeline from '@ui/components/Timeline';
import { ArrowLeft2, ArrowRight2 } from 'iconsax-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PButtonGroup } from './components/ButtonGroup';
import { PCheckboxGroup } from './components/Checkboxes';
import { DocumentPreview } from './components/DocumentPreview';
import { PRadio } from './components/Radio';
import { PSwitch } from './components/Switch';
import { POutlinedField } from './components/TextField';
import useEventPreviewStyles from './useEventPreviewStyles';
import AdditionalAttendeesPreview from './components/AdditionalAttendeesPreview';
const renderField = (field, selectedLanguageId, parentOptionLabelMap, parentFieldLabelMap) => {
    const parentOptionId = field.parentOptionId ?? null;
    const parentFieldLabel = field.parentFieldLabel ?? parentFieldLabelMap?.[parentOptionId || ''] ?? null;
    const parentOptionLabel = (field.parentOptionLabel || parentOptionLabelMap?.[parentOptionId || '']) ?? null;
    const branchedProps = {
        parentOptionId,
        parentFieldLabel: parentFieldLabel || undefined,
        parentOptionLabel: parentOptionLabel || undefined
    };
    switch (field.fieldType) {
        case 'text':
        case 'email':
        case 'phone':
        case 'date':
        case 'time':
            return (_jsx(POutlinedField, { type: 'text', ...branchedProps, children: field.label }, field.id));
        case 'multiline':
            return (_jsx(POutlinedField, { type: 'multiline', ...branchedProps, children: field.label }, field.id));
        case 'dropdown':
        case 'companyDropdown':
        case 'pickupLocation':
        case 'multi-select':
            return (_jsx(POutlinedField, { type: 'select', ...branchedProps, children: field.label }, field.id));
        case 'radio':
            return (_jsx(PRadio, { label: field.label, options: field.options?.map((opt) => opt.label) || [], ...branchedProps }, field.id));
        case 'toggle':
            return _jsx(PSwitch, { label: field.label, ...branchedProps }, field.id);
        case 'checkbox-group':
            return (_jsx(PCheckboxGroup, { label: field.label, options: field.options?.map((opt) => opt.label) || [], ...branchedProps }, field.id));
        case 'choice':
            return (_jsx(PButtonGroup, { label: field.label, options: field.options?.map((opt) => opt.label) || [], ...branchedProps }, field.id));
        case 'undertaking':
            // Get file for the selected language (or fallback to first available)
            let fileName = undefined;
            if (selectedLanguageId) {
                // First check uploaded files by language (existing files from backend)
                const uploadedFilesByLang = field.undertakingUploadedFilesByLanguage || {};
                const uploadedFiles = uploadedFilesByLang[selectedLanguageId];
                if (uploadedFiles && Array.isArray(uploadedFiles) && uploadedFiles.length > 0) {
                    fileName = uploadedFiles[0]?.name;
                }
                // If no uploaded file, check newly uploaded files by language
                if (!fileName) {
                    const filesByLang = field.undertakingFilesByLanguage || {};
                    const files = filesByLang[selectedLanguageId];
                    if (files && Array.isArray(files) && files.length > 0) {
                        fileName = files[0]?.name;
                    }
                }
            }
            // Fallback to legacy single file if no language-specific file found
            if (!fileName) {
                fileName = field.fileData?.name || field.uploadedFileData?.name;
            }
            // Only render DocumentPreview if there's a file
            if (fileName) {
                return (_jsx(DocumentPreview, { label: field.label, fileName: fileName, ...branchedProps }, field.id));
            }
            // Return null if no file is present
            return null;
        case 'additionalAttendees':
            return _jsx(AdditionalAttendeesPreview, { field: field }, field.id);
        default:
            return null;
    }
};
const EventPreview = ({ watch, backgroundImages = [], selectedLanguageId }) => {
    const [showAll, setShowAll] = useState(false);
    const theme = useTheme();
    const companies = watch('companies') || [];
    const formSteps = watch('steps') || [];
    const eventTitle = watch('eventTitle');
    const { t, i18n } = useTranslation();
    const { languages } = useSelector((state) => state.languages);
    const passwordProtection = watch('passwordProtection');
    const parentOptionLabelMap = useMemo(() => {
        const map = {};
        (formSteps || []).forEach((s) => {
            (s.formFields || []).forEach((f) => {
                (f.options || []).forEach((opt) => {
                    map[opt.id] = opt.label;
                });
            });
        });
        return map;
    }, [formSteps]);
    const parentFieldLabelMap = useMemo(() => {
        const map = {};
        (formSteps || []).forEach((s) => {
            (s.formFields || []).forEach((f) => {
                (f.options || []).forEach((opt) => {
                    map[opt.id] = f.label ?? '';
                });
            });
        });
        return map;
    }, [formSteps]);
    // Get language ID from application language (i18n) as fallback
    const getApplicationLanguageId = () => {
        const filteredLanguages = filterApplicationLanguages(languages);
        if (!filteredLanguages || filteredLanguages.length === 0)
            return undefined;
        const normalized = (i18n.language || '').toLowerCase();
        const variants = normalized ? [normalized, normalized.split('-')[0]] : [];
        const matchingLanguage = filteredLanguages.find((lang) => {
            const code = (lang.code || '').toLowerCase();
            return variants.includes(code);
        });
        return matchingLanguage?.id;
    };
    // Use selectedLanguageId if provided, otherwise fallback to application language ID
    const effectiveLanguageId = selectedLanguageId || getApplicationLanguageId();
    const chipRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const checkScroll = () => {
        const el = chipRef.current;
        if (!el)
            return;
        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth);
    };
    const scroll = (direction) => {
        if (!chipRef.current)
            return;
        const scrollAmount = 150;
        chipRef.current.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth'
        });
        // Allow smooth scroll to complete before re-checking
        setTimeout(checkScroll, 300);
    };
    useEffect(() => {
        const el = chipRef.current;
        if (!el)
            return;
        const updateScrollButtons = () => {
            setCanScrollLeft(el.scrollLeft > 0);
            setCanScrollRight(el.scrollWidth > el.clientWidth && el.scrollLeft + el.clientWidth < el.scrollWidth);
        };
        updateScrollButtons(); // run initially
        el.addEventListener('scroll', updateScrollButtons);
        window.addEventListener('resize', updateScrollButtons);
        return () => {
            el.removeEventListener('scroll', updateScrollButtons);
            window.removeEventListener('resize', updateScrollButtons);
        };
    }, [companies, showAll]);
    const visibleCompanies = showAll ? companies : companies.slice(0, 1);
    const hiddenCount = companies.length - 1;
    // Find selected or background image
    let previewImage = EventPreviewThumbnail;
    let isVideoFile = false;
    if (backgroundImages.length > 0) {
        const selected = backgroundImages.find((img) => img.isSelected || img.isBackgroundImage || img.isBackground);
        if (selected) {
            if (selected.file) {
                previewImage = URL.createObjectURL(selected.file);
                isVideoFile = isVideo(selected.file.type, undefined);
            }
            else if (selected.url) {
                previewImage = selected.url;
                isVideoFile = isVideo(undefined, selected.url);
            }
        }
    }
    const styles = useEventPreviewStyles(showAll);
    return (_jsxs(Box, { sx: { ...styles.main }, children: [_jsxs(Box, { sx: { ...styles.eventPreviewContainer }, children: [isVideoFile ? (_jsx(Box, { component: 'video', src: previewImage, autoPlay: true, muted: true, loop: true, playsInline: true, sx: {
                            ...styles.eventPreviewThumbnail,
                            objectFit: 'cover'
                        } })) : (_jsx(Box, { component: 'img', src: previewImage, alt: 'event-thumbnail', sx: { ...styles.eventPreviewThumbnail } })), _jsxs(Box, { sx: { ...styles.titleContainer }, children: [_jsx(Box, { sx: { ...styles.titleWithLanguageContainer }, children: _jsx(Typography, { variant: 'h5', fontWeight: 500, color: 'textPrimary', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', children: eventTitle }) }), _jsxs(Box, { children: [_jsxs(Box, { display: 'flex', alignItems: 'center', position: 'relative', width: '100%', children: [canScrollLeft && (_jsx(IconButton, { onClick: () => scroll('left'), disableRipple: true, disableFocusRipple: true, disableTouchRipple: true, size: 'small', sx: {
                                                    p: 0,
                                                    minWidth: 'auto',
                                                    backgroundColor: 'transparent',
                                                    '&:hover': {
                                                        backgroundColor: 'transparent'
                                                    },
                                                    border: 'none',
                                                    boxShadow: 'none'
                                                }, children: _jsx(ArrowLeft2, { fontSize: 'small', style: { color: theme.palette.primary.dark } }) })), _jsxs(Box, { ref: chipRef, sx: styles.chipContainer, children: [visibleCompanies?.map((company, index) => (_jsx(Chip, { variant: 'soft', color: 'primary', label: company.label, size: 'small' }, index))), !showAll && hiddenCount > 0 && (_jsx(Chip, { label: `${hiddenCount}+`, size: 'small', variant: 'outlined', onClick: () => {
                                                            setShowAll(true);
                                                            setTimeout(() => {
                                                                const el = chipRef.current;
                                                                if (!el)
                                                                    return;
                                                                setCanScrollRight(el.scrollWidth > el.clientWidth);
                                                            }, 50);
                                                        }, sx: { cursor: 'pointer' } }))] }), canScrollRight && (_jsx(IconButton, { onClick: () => scroll('right'), disableRipple: true, disableFocusRipple: true, disableTouchRipple: true, size: 'small', sx: {
                                                    p: 0,
                                                    minWidth: 'auto',
                                                    backgroundColor: 'transparent',
                                                    '&:hover': {
                                                        backgroundColor: 'transparent'
                                                    },
                                                    border: 'none',
                                                    boxShadow: 'none'
                                                }, children: _jsx(ArrowRight2, { fontSize: 'small', style: { color: theme.palette.primary.dark } }) }))] }), _jsxs(Typography, { variant: 'caption', fontWeight: 400, color: theme.palette.text.primary, children: [t('labels.password'), ' ', passwordProtection ? t('labels.protected') : t('labels.notProtected')] })] })] })] }), _jsx(Box, { sx: { ...styles.timelineContainer }, children: _jsx(HTimeline, { options: formSteps && formSteps.length > 0
                        ? formSteps
                            .filter((step) => 
                        // Only show steps that have actual content (formFields with length > 0)
                        // This filters out default placeholder steps that have no content
                        step.formFields && step.formFields.length > 0)
                            .map((step, idx) => ({
                            step: step.name,
                            content: (_jsx(Box, { sx: { ...styles.fieldsContainer }, children: step?.formFields?.map((field) => renderField(field, effectiveLanguageId, parentOptionLabelMap, parentFieldLabelMap)) }))
                        }))
                        : [] // Show empty timeline instead of static steps when no steps exist
                 }) })] }));
};
export default EventPreview;
